import { isValid } from 'date-fns';
import { INITIAL_RANGE, navigateSelectedDate } from '../dateRange/useDateRangeUseCase';
import formatDate from '../../utils/formatDate';

export { navigateSelectedDate };

/**
 * Build a local calendar date and reject overflow (e.g. 31/02/2026).
 * @param {number} year
 * @param {number} monthIndex
 * @param {number} day
 * @returns {Date | null}
 */
const createCalendarDate = (year, monthIndex, day) => {
  const date = new Date(year, monthIndex, day);
  if (
    !isValid(date)
    || date.getFullYear() !== year
    || date.getMonth() !== monthIndex
    || date.getDate() !== day
  ) {
    return null;
  }
  return date;
};

/**
 * Parse a date string returned by the API into a Date.
 * Supports YYYY-MM-DD, DD/MM/YYYY, ISO 8601, and YYYYMMDD (e.g. "20260424").
 * @param {string | null | undefined} value - Date string from API
 * @returns {Date | null} Parsed date or null when invalid/missing
 */
export const parseApiDateString = (value) => {
  if (value === null || value === undefined || value === '') return null;

  const str = String(value).trim();

  if (/^\d{8}$/.test(str)) {
    const year = Number(str.slice(0, 4));
    const month = Number(str.slice(4, 6)) - 1;
    const day = Number(str.slice(6, 8));
    return createCalendarDate(year, month, day);
  }

  if (/^\d{4}-\d{2}-\d{2}$/.test(str)) {
    const [year, month, day] = str.split('-').map(Number);
    return createCalendarDate(year, month - 1, day);
  }

  if (/^\d{2}\/\d{2}\/\d{4}$/.test(str)) {
    const [day, month, year] = str.split('/').map(Number);
    return createCalendarDate(year, month - 1, day);
  }

  const date = new Date(str);
  return isValid(date) ? date : null;
};

/**
 * Normalize an API date string to YYYY-MM-DD for POST requests.
 * @param {string | null | undefined} value - Date string from API
 * @returns {string | undefined} Normalized date string
 */
export const normalizeApiDateStringForPost = (value) => {
  const date = parseApiDateString(value);
  if (!date) return undefined;
  return formatDate(date, 'yyyy-MM-dd', 'fr', '');
};

/**
 * Extract relevant global filters for query key
 * @param {ApiManagerGlobalFilters | undefined} globalFilters - Global filters
 * @returns {GlobalFiltersKey | null} Stable key object for query key
 */
export const getGlobalFiltersKey = (globalFilters) => {
  if (!globalFilters) return null;

  return {
    dateRange: globalFilters.dateRange ? {
      type: globalFilters.dateRange.type,
      rangeStartDate: globalFilters.dateRange.rangeStartDate,
      rangeEndDate: globalFilters.dateRange.rangeEndDate,
      selectedDate: globalFilters.dateRange.selectedDate,
    } : null,
    filterValues: globalFilters.filterValues
      ? JSON.stringify(globalFilters.filterValues)
      : null,
    personalViews: globalFilters.personalViews?.selectedOption || null,
  };
};

/**
 * Map API dateRange to react-date-range UI ranges
 * @param {ApiManagerDateRangeFilter | undefined} dateRange - Date range from GET response
 * @returns {import('react-date-range').Range[]} UI ranges
 */
export const mapApiDateRangeToUiRange = (dateRange) => {
  const startDate = parseApiDateString(dateRange?.rangeStartDate) ?? new Date();
  const endDate = parseApiDateString(dateRange?.rangeEndDate) ?? new Date();

  return [{
    startDate,
    endDate,
    key: INITIAL_RANGE.key,
  }];
};

/**
 * Build dateRange payload for POST request
 * @param {object} params
 * @param {ApiManagerDateRangeType} params.type - Date range type
 * @param {import('react-date-range').Range} params.range - Computed range
 * @param {Date} params.selectedDate - Last clicked/anchored date
 * @returns {ApiManagerGlobalFiltersDateRange} Date range for POST request
 */
export const buildDateRangePostPayload = ({ type, range, selectedDate }) => ({
  type,
  rangeStartDate: formatDate(range.startDate, 'yyyy-MM-dd', 'fr'),
  rangeEndDate: formatDate(range.endDate, 'yyyy-MM-dd', 'fr'),
  selectedDate: formatDate(selectedDate, 'yyyy-MM-dd', 'fr'),
});

/**
 * Extract dateRange for POST request (without typeOptions)
 * @param {ApiManagerDateRangeFilter | undefined} dateRange - Date range from GET response
 * @returns {ApiManagerGlobalFiltersDateRange | undefined} Date range for POST request
 */
export const extractDateRangeForPost = (dateRange) => {
  if (!dateRange) return undefined;

  const selectedRaw = dateRange.selectedDate ?? dateRange.rangeStartDate;

  return {
    type: dateRange.type,
    rangeStartDate: dateRange.rangeStartDate,
    rangeEndDate: dateRange.rangeEndDate,
    selectedDate: normalizeApiDateStringForPost(selectedRaw)
      ?? normalizeApiDateStringForPost(dateRange.rangeStartDate)
      ?? dateRange.rangeStartDate,
  };
};
